import React, { useState } from "react";
import { Copy, Check, CreditCard, Building2, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export interface CorporateBankCardProps {
  bankName?: string;
  accountNumber?: string;
  accountName?: string;
  branch?: string;
  currency?: string;
  swiftCode?: string;
  className?: string;
}

export const CorporateBankCard: React.FC<CorporateBankCardProps> = ({
  bankName = "MUFG Bank, Ltd. - Chi nhánh Hà Nội",
  accountNumber = "040436",
  accountName = "CÔNG TY TNHH ARTPRESTO VIỆT NAM",
  branch = "Chi nhánh Hà Nội",
  currency = "VND / USD",
  swiftCode = "BOTKVNVX",
  className = "",
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyAccount = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!accountNumber) return;
    navigator.clipboard.writeText(accountNumber.replace(/\s+/g, ""));
    setCopied(true);
    toast.success(`Đã sao chép số tài khoản: ${accountNumber}`);
    setTimeout(() => setCopied(false), 2000);
  };

  const cleanAcc = (accountNumber || '').replace(/\s+/g, '');
  const isLongAcc = cleanAcc.length > 13;

  return (
    <div
      className={`relative w-full overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 p-5 sm:p-6 text-white shadow-xl shadow-slate-950/25 border border-slate-700/60 flex flex-col justify-between min-h-[220px] select-none group transition-all duration-300 hover:shadow-2xl hover:border-indigo-500/40 ${className}`}
    >
      {/* Background Decorative Guilloche / Laser Pattern */}
      <div className="absolute -right-12 -bottom-12 w-48 h-48 rounded-full bg-indigo-500/10 blur-2xl pointer-events-none" />
      <div className="absolute -left-12 -top-12 w-40 h-40 rounded-full bg-amber-500/10 blur-2xl pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(#ffffff0a_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none opacity-60" />

      {/* Top Row: Brand & Card Type */}
      <div className="relative z-10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 shrink-0">
            <Building2 className="w-4 h-4 text-amber-300" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-extrabold tracking-widest text-amber-400 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" /> Corporate Account
            </span>
            <span className="text-[10px] font-mono text-slate-300">
              {swiftCode ? `SWIFT: ${swiftCode}` : 'BUSINESS BANKING'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 text-[10px] font-mono font-semibold px-2.5 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-200">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
          <span>VERIFIED</span>
        </div>
      </div>

      {/* Row: Bank Name & Branch on independent line */}
      <div className="relative z-10 pt-2">
        <h4 className="text-xs sm:text-sm font-extrabold tracking-tight text-white/95 uppercase leading-snug break-words">
          {bankName}
        </h4>
      </div>

      {/* Middle Row: Gold Chip & Contactless */}
      <div className="relative z-10 my-3 flex items-center justify-between">
        {/* EMV Metallic Gold Chip */}
        <div className="relative w-11 h-8 rounded-md bg-gradient-to-tr from-amber-400 via-amber-200 to-yellow-500 p-[1px] shadow-md border border-amber-300/80">
          <div className="w-full h-full rounded-[5px] bg-gradient-to-br from-amber-400 to-amber-600/90 flex flex-col justify-between p-1">
            <div className="h-[1px] w-full bg-amber-800/40" />
            <div className="h-[1px] w-full bg-amber-800/40" />
          </div>
          <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-amber-800/40" />
        </div>

        {/* Contactless Wave / NFC */}
        <div className="flex items-center gap-1 text-slate-400">
          <CreditCard className="w-5 h-5 text-slate-300/80" />
          <svg className="w-5 h-5 text-slate-300/70 rotate-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 9a12 12 0 0 1 16 0" />
            <path d="M7 12a8 8 0 0 1 10 0" />
            <path d="M10 15a4 4 0 0 1 4 0" />
          </svg>
        </div>
      </div>

      {/* Bottom Area: Account Number & Cardholder Name */}
      <div className="relative z-10 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">
              Số tài khoản doanh nghiệp
            </div>
            <div className={`font-mono ${isLongAcc ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'} font-black tracking-[0.15em] sm:tracking-[0.2em] text-amber-100/95 drop-shadow select-all`}>
              {accountNumber || "0000 0000 0000"}
            </div>
          </div>

          <button
            type="button"
            onClick={handleCopyAccount}
            title="Sao chép số tài khoản"
            aria-label="Sao chép số tài khoản"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md text-xs font-semibold text-white transition-all active:scale-95 shadow-sm cursor-pointer shrink-0"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[11px] text-emerald-300">Đã copy</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5 text-amber-300" />
                <span className="text-[11px]">Copy</span>
              </>
            )}
          </button>
        </div>

        <div className="flex items-end justify-between gap-3 pt-2 border-t border-white/10 text-[11px]">
          <div className="min-w-0 flex-1">
            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
              Chủ tài khoản thụ hưởng
            </div>
            <div className="font-extrabold text-slate-100 text-xs sm:text-sm tracking-tight uppercase leading-snug break-words">
              {accountName || "CÔNG TY TNHH ARTPRESTO VIỆT NAM"}
            </div>
          </div>

          <div className="text-right shrink-0">
            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
              Tiền tệ / Chi nhánh
            </div>
            <div className="font-mono font-medium text-amber-200/90 text-[11px]">
              {currency} • {branch}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CorporateBankCard;
